import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { IndividualConfig, ToastrService } from 'ngx-toastr';

import { flagPanels } from '../../modules/configurator/store/configurator/configurator.store';
// Status 0: the request never reached the server
const HTTP_ERROR_TEXT: Record<number, string> = {
	0: 'Geen verbinding met de server. Controleer uw internetverbinding.',
	400: 'De gegevens werden niet aanvaard. Controleer uw invoer en probeer opnieuw.',
	401: 'Uw sessie is verlopen. Meld u opnieuw aan.',
	404: 'Dit bestaat niet meer. Vernieuw de pagina.',
	412: 'Dit werd intussen elders gewijzigd. Vernieuw de pagina en probeer opnieuw.',
	500: 'Er ging iets mis op de server. Probeer het later opnieuw.',
	503: 'De dienst is even niet bereikbaar. Probeer het over enkele minuten opnieuw.',
};

/** The one way the app shows a toast; HTTP errors arrive here through the error interceptor. */
@Injectable({
	providedIn: 'root',
})
export class AlertService {
	constructor(private toastService: ToastrService) {}

	public success(message: string, title?: string, options?: Partial<IndividualConfig>): void {
		this.toastService.success(message, title, options);
	}

	public info(message: string, title?: string, options?: Partial<IndividualConfig>): void {
		this.toastService.info(message, title, options);
	}

	public warning(message: string, title?: string, options?: Partial<IndividualConfig>): void {
		this.toastService.warning(message, title, options);
	}

	public error(message: string, title?: string, options?: Partial<IndividualConfig>): void {
		this.toastService.error(message, title, options);
	}

	handleNotices(notices: string[]) {
		notices.forEach(notice => this.toastService.info(notice));
	}

	/** The one toast of a failed HTTP call: the server's own reason where it gives one, else a Dutch text per status. */
	handleError(err: HttpErrorResponse) {
		if (err.status === HttpStatusCode.Forbidden) {
			return;
		}
		// A panel that no longer fits its board: glow it while the toast is up, so the user
		// sees which one and can revisit its grain lock.
		if (err.status === HttpStatusCode.Conflict && err.error?.panelId) {
			flagPanels([err.error.panelId], this.toastService.toastrConfig.timeOut);
		}
		// The service's own reason, where it speaks to the user
		const reason = [HttpStatusCode.BadRequest, HttpStatusCode.Conflict].includes(err.status)
			? err.error?.message ?? err.error?.detail
			: null;
		const serverText = typeof reason === 'string' && reason.trim() ? reason : null;
		this.toastService.error(serverText ?? HTTP_ERROR_TEXT[err.status] ?? `Er ging iets mis (fout ${err.status}). Probeer het opnieuw.`);
	}
}
